import React, { useContext, useEffect } from 'react';
import RenderContent from '@/ui/templates/TaxGuideContent/RenderContent';
import DOMPurify from 'dompurify';
import { Box, Stack } from '@mui/material';
import { TaxGuideContext } from '@/state/TaxGuideProvider';
import useTaxCalculationPopup from '@/ui/hooks/useTaxCalculationModal';
import { ModalContext } from '@/state/ModalProvider';
import SampleTaxCalculation from '@/ui/templates/TaxGuideContent/SampleTaxCalculation';
import LoadingShimmer from '../LoadingShimmer';
import { TaxAdvantage } from '@/ui/molecules/InputSection/AtomicComponents/TaxAdvantage';
import DownloadButton from '@/ui/molecules/InputSection/AtomicComponents/DownloadButton';
import { NotFoundComponent } from '@/ui/templates/NotFound/NotFoundComponent';

type Props = {
  country: string;
  plan: string;
  state: string;
};

// sample url  http://localhost:3000/preview?countryId=be&userId=5055&planId=4793&type=client&companyId=4709

const ClientPreview = (props: Props) => {
  const { country, plan, state } = props;

  const {
    content,
    updateRequest,
    isPending,
    isFetching,
    downloadPDFCall,
    downloadWordCall,
    isDownloading,
    isDownloadingWord,
  } = useContext(TaxGuideContext);

  const modalStatus = useContext(ModalContext);

  const wordDownloadStatus = content?.data.isWordDownloadEnabled;
  const pdfDownloadStatus = content?.data.isPdfEnabled;

  useEffect(() => {
    updateRequest({
      planId: plan,
      countryId: country,
      stateId: state,
      taxAdvantage: null,
      taxCalculationPopupList: null,
    });
  }, [country, plan, state]);

  useTaxCalculationPopup({ content, modalStatus });

  if (isPending && isFetching)
    return <LoadingShimmer call="partial" shimmerWidth={'95vw'} />;

  if (!content?.data.content)
    return (
      <>
        <NotFoundComponent />
      </>
    );

  const sanitizedBlog = DOMPurify.sanitize(content.data.content);

  return (
    <>
      <Stack
        style={{
          padding: 12,
          margin: 4,
        }}
        direction={'row'}
        spacing={4}
      >
        <TaxAdvantage
          taxAdvResp={content?.data.taxAdvantage}
          onChange={toggleValue => {
            if (content?.data.taxAdvantage) {
              updateRequest({
                taxAdvantage: {
                  displayToggle: toggleValue,
                  approvedConditionsId:
                    content.data.taxAdvantage.approvedConditionsId,
                },
              });
            }
          }}
        />
        {pdfDownloadStatus && (
          <DownloadButton
            downloadCall={downloadPDFCall}
            isDownloading={isDownloading}
            content={content}
            buttonText="Download PDF"
          />
        )}
        {wordDownloadStatus && (
          <DownloadButton
            downloadCall={downloadWordCall}
            isDownloading={isDownloadingWord}
            content={content}
            buttonText="Download WORD"
          />
        )}
      </Stack>
      <Box
        display={'flex'}
        justifyContent={'center'}
        id={'taxGuideContent'}
        style={{
          padding: 12,
          margin: 4,
          width: '90vw',
        }}
      >
        <RenderContent
          htmlString={sanitizedBlog}
          chartContents={content.data.chartContents}
        />
        {modalStatus.showModal && <SampleTaxCalculation />}
      </Box>
    </>
  );
};

export default ClientPreview;
